import { useCallback, useEffect } from "react";

import { ContactSupportLine } from "@/features/app/components/ContactSupportLine";
import { logger } from "@/lib/logging/logger";
import { AdminAlert, AdminAlertDescription, AdminAlertTitle } from "@/shared/design-system/admin/AdminAlert";
import { AdminButton } from "@/shared/design-system/admin/AdminButton";
import { StatusPage } from "@/shared/layout/StatusPage";
import { InternalLink } from "@/shared/ui/internal-link";

type ErrorPageProps = {
  error?: Error;
  onRetry?: () => void;
};

const log = logger.child("ErrorPage");

export function ErrorPage({ error, onRetry }: ErrorPageProps) {
  useEffect(() => {
    if (error) {
      log.error("App error boundary caught", { error, suppressTelemetry: true });
    }
  }, [error]);

  const handleRetry = useCallback(() => {
    if (onRetry) {
      onRetry();
      return;
    }

    if (typeof window !== "undefined") {
      window.location.reload();
    }
  }, [onRetry]);

  const handleBackHome = useCallback(() => {
    onRetry?.();
  }, [onRetry]);

  return (
    <StatusPage
      kind="error"
      title="Something went wrong"
      subtitle="We encountered an unexpected error. Our team has been notified and we're working on fixing it."
      onRetry={handleRetry}
      retryAnalyticsElementId="app-error:retry"
      actions={
        <AdminButton asChild variant="outline">
          <InternalLink analyticsElementId="app-error:back-home" href="/" onClick={handleBackHome}>
            Go to homepage
          </InternalLink>
        </AdminButton>
      }
    >
      {process.env.NODE_ENV === "development" && error?.message ? (
        <AdminAlert variant="danger" className="text-left">
          <AdminAlertTitle>Error details</AdminAlertTitle>
          <AdminAlertDescription>{error.message}</AdminAlertDescription>
        </AdminAlert>
      ) : null}

      <ContactSupportLine lead="Need help?" analyticsElementId="app-error:contact-email" />
    </StatusPage>
  );
}
